import { useCallback, useRef, type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import type { TranscriptItem } from 'types'
import type { FunnelStage } from '../../core/types/funnel-stage.js'
import {
  applyOrchestratorTurnResult,
  consumeOrchestratorTriggerMetadata,
  type SummaryAgentOutput,
} from '../../lib/apply-orchestrator-turn.js'
import { isExplicitBookingAction } from '../../core/agents/consultation-intent-router.js'
import { LIVE_INFERENCE_TIMEOUT_MS, ORCHESTRATOR_FALLBACK } from '../../core/contracts.js'
import { formatBookingLinkResponse } from '../../lib/ensure-booking-link.js'
import { logger } from '../../lib/logger-client.js'
import { generateTranscriptId } from '../../lib/transcript-append.js'
import { liveRuntimeTrace, sessionIdPrefix } from '../../lib/live-runtime-trace.js'

interface GateTurnOwnerRef {
  loadingId: string
  startedAt: number
}

export interface UseLiveGateTurnHandlerParams {
  sessionId: string
  transcriptRef: MutableRefObject<TranscriptItem[]>
  currentLoadingIdRef: MutableRefObject<string | null>
  restTurnOwnerRef?: MutableRefObject<GateTurnOwnerRef | null>
  reportTimeoutRef?: MutableRefObject<ReturnType<typeof setTimeout> | null>
  clientSessionViewStore: {
    getSnapshot: () => {
      intelligenceContext?: Record<string, unknown> | object
      transcript: TranscriptItem[]
    }
  }
  setTranscript: Dispatch<SetStateAction<TranscriptItem[]>>
  setIsThinking: (value: boolean) => void
  setBackendStatus: Dispatch<
    SetStateAction<{
      mode: 'idle' | 'agents' | 'fallback' | 'voice'
      message: string
      severity: 'info' | 'warn' | 'error'
    }>
  >
  setCurrentReasoning: Dispatch<SetStateAction<string>>
  setActiveTools: Dispatch<SetStateAction<any[]>>
  applyServerTurnResult: (params: {
    transcript: TranscriptItem[]
    backendStatus: {
      mode: 'agents'
      message: string
      severity: 'info'
    }
  }) => void
  setCurrentAgentStage?: (stage: FunnelStage) => void
  handleGenerateDiscoveryReport: (summary?: SummaryAgentOutput, source?: 'click' | 'auto' | 'live-tool') => void
}

export function useLiveGateTurnHandler(params: UseLiveGateTurnHandlerParams) {
  const pendingMetadataRef = useRef<Record<string, unknown>>({})
  const pendingAgentRef = useRef('Discovery Agent')
  const finalizedTurnIdsRef = useRef(new Set<string>())
  const gateTurnTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearGateTurnTimeout = useCallback(() => {
    if (gateTurnTimeoutRef.current) {
      clearTimeout(gateTurnTimeoutRef.current)
      gateTurnTimeoutRef.current = null
    }
  }, [])

  const finalizeGateTurn = useCallback(
    (payload: { text: string; turnId?: string }) => {
      clearGateTurnTimeout()
      if (payload.turnId && finalizedTurnIdsRef.current.has(payload.turnId)) {
        return
      }
      if (payload.turnId) {
        finalizedTurnIdsRef.current.add(payload.turnId)
      }

      const loadingId =
        params.currentLoadingIdRef.current || params.restTurnOwnerRef?.current?.loadingId

      if (!loadingId) {
        params.setIsThinking(false)
        pendingMetadataRef.current = {}
        return
      }

      const rawText = payload.text.trim()
      const outputText = rawText || ORCHESTRATOR_FALLBACK
      if (!rawText) {
        logger.warn('[LiveGate] Empty gate turn text — applying orchestrator fallback', {
          turnId: payload.turnId,
          loadingIdPrefix: loadingId.slice(0, 8),
          fallback_fired: true,
          trigger: 'gate_empty',
        })
        liveRuntimeTrace({
          event: 'gate_turn_empty_fallback',
          route: 'live',
          restTurnOwnerActive: Boolean(params.restTurnOwnerRef?.current),
          sessionPrefix: sessionIdPrefix(params.sessionId),
          extra: { turnId: payload.turnId, loadingIdPrefix: loadingId.slice(0, 8) },
        })
      }

      const liveModelItem = params.transcriptRef.current.find(
        item =>
          item.role === 'model' &&
          item.isLiveTranscript === true &&
          item.text.trim().length > 0 &&
          (item.id === loadingId || (payload.turnId && item.turnId === payload.turnId))
      )
      const resultLoadingId = liveModelItem?.id ?? loadingId
      if (resultLoadingId !== loadingId) {
        params.transcriptRef.current = params.transcriptRef.current.filter(
          item => item.id !== loadingId
        )
      }

      const metadata = rawText
        ? { ...pendingMetadataRef.current }
        : { degraded: true, fallback_fired: true, trigger: 'gate_empty' }
      const turnResult = applyOrchestratorTurnResult(
        {
          output: outputText,
          agent: pendingAgentRef.current,
          metadata,
        },
        {
          clientSessionViewStore: params.clientSessionViewStore,
          transcriptRef: params.transcriptRef,
          currentHistory: params.transcriptRef.current,
          loadingId: resultLoadingId,
          ...(payload.turnId ? { turnId: payload.turnId } : {}),
          ...(liveModelItem ? { preserveExistingModelText: true } : {}),
        }
      )

      params.setActiveTools(prev => prev.filter(tool => tool.status === 'complete'))
      params.setCurrentReasoning('')
      params.currentLoadingIdRef.current = null
      if (params.restTurnOwnerRef?.current?.loadingId === loadingId) {
        params.restTurnOwnerRef.current = null
      }
      params.setIsThinking(false)

      params.applyServerTurnResult({
        transcript: turnResult.transcript,
        backendStatus: turnResult.backendStatus,
      })

      if (turnResult.triggerDiscoveryReport) {
        if (params.reportTimeoutRef) {
          if (params.reportTimeoutRef.current) {
            clearTimeout(params.reportTimeoutRef.current)
          }
          params.reportTimeoutRef.current = setTimeout(() => {
            params.reportTimeoutRef!.current = null
            params.handleGenerateDiscoveryReport(turnResult.summaryAgentOutput, 'live-tool')
          }, 0)
        } else {
          params.handleGenerateDiscoveryReport(turnResult.summaryAgentOutput, 'live-tool')
        }
      }

      consumeOrchestratorTriggerMetadata(metadata)
      pendingMetadataRef.current = {}

      liveRuntimeTrace({
        event: 'gate_turn_complete',
        route: 'live',
        restTurnOwnerActive: false,
        sessionPrefix: sessionIdPrefix(params.sessionId),
        extra: {
          turnId: payload.turnId,
          triggerDiscoveryReport: turnResult.triggerDiscoveryReport,
          triggerBooking: turnResult.triggerBooking,
        },
      })
    },
    [clearGateTurnTimeout, params]
  )

  const beginGateTurn = useCallback(() => {
    clearGateTurnTimeout()
    const loadingId = generateTranscriptId('assistant')
    params.currentLoadingIdRef.current = loadingId
    pendingMetadataRef.current = {}
    pendingAgentRef.current = 'Discovery Agent'
    params.setIsThinking(true)
    params.setActiveTools([])
    params.setCurrentReasoning('')
    params.setBackendStatus({
      mode: 'agents',
      message: 'Routing via Multi-Agent...',
      severity: 'info',
    })
    params.setTranscript(prev => [
      ...prev,
      {
        id: loadingId,
        role: 'model',
        text: '',
        timestamp: new Date(),
        isFinal: false,
        status: 'streaming',
      },
    ])
    liveRuntimeTrace({
      event: 'gate_turn_begin',
      route: 'live',
      restTurnOwnerActive: false,
      sessionPrefix: sessionIdPrefix(params.sessionId),
      extra: { loadingIdPrefix: loadingId.slice(0, 8) },
    })
    gateTurnTimeoutRef.current = setTimeout(() => {
      gateTurnTimeoutRef.current = null
      if (params.currentLoadingIdRef.current !== loadingId) return
      const lastUserText = [...params.transcriptRef.current]
        .reverse()
        .find(item => item.role === 'user')
        ?.text
      if (lastUserText && isExplicitBookingAction(lastUserText)) {
        logger.warn('[LiveGate] Gate turn timed out on an explicit book ask — painting calendar', {
          loadingIdPrefix: loadingId.slice(0, 8),
          timeoutMs: LIVE_INFERENCE_TIMEOUT_MS,
        })
        pendingMetadataRef.current = {
          triggerBooking: true,
          action: 'show_calendar_widget',
          stage: 'BOOKING_REQUESTED',
          intent: 'booking_request',
        }
        finalizeGateTurn({ text: formatBookingLinkResponse() })
        return
      }
      logger.warn('[LiveGate] Gate turn timed out — applying orchestrator fallback', {
        loadingIdPrefix: loadingId.slice(0, 8),
        timeoutMs: LIVE_INFERENCE_TIMEOUT_MS,
      })
      liveRuntimeTrace({
        event: 'gate_turn_timeout_fallback',
        route: 'live',
        restTurnOwnerActive: Boolean(params.restTurnOwnerRef?.current),
        sessionPrefix: sessionIdPrefix(params.sessionId),
        extra: { loadingIdPrefix: loadingId.slice(0, 8), timeoutMs: LIVE_INFERENCE_TIMEOUT_MS },
      })
      finalizeGateTurn({ text: '' })
    }, LIVE_INFERENCE_TIMEOUT_MS)
    return loadingId
  }, [clearGateTurnTimeout, finalizeGateTurn, params])

  const handleGateStageUpdate = useCallback(
    (payload: {
      stage?: string
      agent?: string
      metadata?: Record<string, unknown>
      turnId?: string
    }) => {
      if (payload.metadata) {
        pendingMetadataRef.current = {
          ...pendingMetadataRef.current,
          ...payload.metadata,
        }
      }
      if (payload.agent) {
        pendingAgentRef.current = payload.agent
      }
      if (payload.stage && params.setCurrentAgentStage) {
        params.setCurrentAgentStage(payload.stage as FunnelStage)
      }
    },
    [params]
  )

  const abortGateTurn = useCallback(() => {
    clearGateTurnTimeout()
    const loadingId =
      params.currentLoadingIdRef.current ?? params.restTurnOwnerRef?.current?.loadingId ?? null

    params.currentLoadingIdRef.current = null
    if (params.restTurnOwnerRef) {
      params.restTurnOwnerRef.current = null
    }
    params.setIsThinking(false)
    pendingMetadataRef.current = {}

    if (!loadingId) return

    params.setTranscript(prev => {
      const next = prev.filter(item => {
        if (item.id === loadingId) return false
        if (
          item.role === 'model' &&
          item.status === 'streaming' &&
          !item.text?.trim() &&
          !item.isFinal
        ) {
          return false
        }
        return true
      })
      return next.length === prev.length ? prev : next
    })

    liveRuntimeTrace({
      event: 'gate_turn_aborted',
      route: 'live',
      restTurnOwnerActive: false,
      sessionPrefix: sessionIdPrefix(params.sessionId),
      extra: { loadingIdPrefix: loadingId.slice(0, 8) },
    })
  }, [clearGateTurnTimeout, params])

  return {
    beginGateTurn,
    abortGateTurn,
    handleGateStageUpdate,
    finalizeGateTurn,
  }
}
